import { useMemo, useState, useCallback } from 'react';
import tw from 'twin.macro';
import { Tooltip } from 'flowbite-react';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { Badge, Button, Spinner, Title } from '@restworld/ui-ds';
import { Address, Variable, WorkerJPSavedResearch } from '@restworld/utility-types';
import {
  defaultItalyAddressMetaData,
  useNotificationFrequencyOptions,
  useResponsiveBreakPoints,
} from '@restworld/utils-common';

import {
  notificationModalStateAtom,
  notificationModalToggleAtom,
  notificationResearchIdAtom,
  SaveResearchFrequency,
  notificationChangeAtom,
  userUnsubscribeReasonAtom,
  userUnsubscribeReasonFreeTextAtom,
  savedResearchDataAtom,
  occupationDataAtom,
} from './save-research-tab.atoms';
import SaveResearchSubscribeToggle from './save-research-subscribe-toggle';

const SavedResearch = ({
  data,
  domainInterest,
}: {
  data: Partial<WorkerJPSavedResearch>;
  domainInterest?: Variable[];
}) => {
  const [loading, onLoading] = useState(false);

  const setNotificationToggle = useSetAtom(notificationModalToggleAtom);
  const setNotificationResearchId = useSetAtom(notificationResearchIdAtom);
  const setNotificationModalState = useSetAtom(notificationModalStateAtom);
  const setNotificationChange = useSetAtom(notificationChangeAtom);
  const setUserUnsubscribeReason = useSetAtom(userUnsubscribeReasonAtom);
  const setUserUnsubscribeReasonText = useSetAtom(userUnsubscribeReasonFreeTextAtom);
  const setSavedResearch = useSetAtom(savedResearchDataAtom);
  const [{ data: occupationData }] = useAtom(occupationDataAtom);
  const { t } = useTranslation();

  const notificationFrequencyOptions = useNotificationFrequencyOptions();
  const { isDesktop } = useResponsiveBreakPoints();

  const filterParams = useMemo(() => data.filter_params, [data.filter_params]);
  const address = useMemo(() => filterParams?.address as Address, [filterParams?.address]);
  const isSeasonalAccommodation = useMemo(
    () => filterParams?.contract_type?.includes('seasonal_accommodation'),
    [filterParams?.contract_type]
  );

  const domainInterestIds = useMemo(() => filterParams?.domain_interest as string[], [filterParams?.domain_interest]);

  const domainInterestFirstIndex = useMemo(() => {
    const names = domainInterestIds?.map((id) => domainInterest?.find((item) => item.id === id)?.label);
    return names?.length ? names[0] : '';
  }, [domainInterest, domainInterestIds]);

  const occupationIds = useMemo(() => (filterParams?.['occupation_id'] as string[]) ?? [], [filterParams]);

  const occupationIdsFirstIndex = useMemo(() => {
    const names = occupationIds?.map((id) => occupationData?.find((item) => item.id === Number(id))?.label_it.label);
    return names?.length ? names[0] : '';
  }, [occupationData, occupationIds]);

  const frequencyText = useMemo(
    () =>
      data.enabled
        ? notificationFrequencyOptions.find((item) => item.id === data.frequency)?.label
        : t('common:deactivate'),
    [t, data.enabled, data.frequency, notificationFrequencyOptions]
  );

  const frequencyEl = useMemo(() => {
    const title = (
      <Title
        icon="BellIcon"
        content={frequencyText}
        fontWeight="normal"
        fontSize="small"
        twStyle={tw`leading-5.25`}
        textColor="gray-600"
        maxChars={!isDesktop ? 10 : 40}
      />
    );
    if (frequencyText.length > 10 && !isDesktop)
      return (
        <Tooltip style="dark" content={frequencyText}>
          {title}
        </Tooltip>
      );
    return title;
  }, [frequencyText, isDesktop]);

  const onModalOpen = useCallback(() => {
    setSavedResearch(data);
    setNotificationResearchId(data.id);
    // reset previous the modal states
    setNotificationModalState('default');
    setNotificationChange(data.frequency as SaveResearchFrequency);
    setUserUnsubscribeReason('');
    setUserUnsubscribeReasonText('');
    // open the modal
    setNotificationToggle(true);
  }, [
    setNotificationResearchId,
    data,
    setSavedResearch,
    setNotificationModalState,
    setNotificationChange,
    setUserUnsubscribeReason,
    setUserUnsubscribeReasonText,
    setNotificationToggle,
  ]);
  const addressTitle = useMemo(
    () =>
      address?.locality
        ? address.locality === defaultItalyAddressMetaData.locality || address.locality === 'Italy'
          ? t('common:Italy')
          : address.locality
        : address?.address,
    [address?.address, address?.locality, t]
  );
  const title = useMemo(
    () =>
      address
        ? occupationIdsFirstIndex
          ? `${occupationIdsFirstIndex} a ${addressTitle}`
          : addressTitle
        : occupationIdsFirstIndex ||
          `${t('user-settings:saved_search_title', {
            id: `#${data.id}`,
          })}`,
    [address, addressTitle, data.id, occupationIdsFirstIndex, t]
  );

  return (
    <div tw="flex flex-col gap-5 lg:min-w-[500px] relative">
      <div tw="flex flex-col gap-2">
        <Title content={title} fontWeight="semibold" fontSize="base" twStyle={tw`leading-6`} />
        <div tw="flex flex-row justify-between items-center">
          <div tw="flex flex-row gap-2 flex-wrap">
            {domainInterestFirstIndex && <Badge variant="primary">{domainInterestFirstIndex}</Badge>}
            {occupationIdsFirstIndex && <Badge variant="primary">{occupationIdsFirstIndex}</Badge>}
            {address?.locality && <Badge variant="primary">{addressTitle}</Badge>}
            {isSeasonalAccommodation && <Badge variant="primary">Alloggio stagionale</Badge>}
            {domainInterestIds?.length && (
              <Badge variant="primary">+{occupationIds?.length + domainInterestIds.length}</Badge>
            )}
            {!domainInterestFirstIndex &&
              !occupationIdsFirstIndex &&
              !address?.locality &&
              !isSeasonalAccommodation && (
                <Title
                  icon="InformationCircleIcon"
                  content={t('user-settings:no_search_criteria')}
                  fontSize="small"
                  fontWeight="normal"
                  textColor="gray-600"
                />
              )}
          </div>
          <SaveResearchSubscribeToggle onLoading={onLoading} defaultChecked={data.enabled} saveResearchId={data.id} />
        </div>
      </div>
      <div tw="flex gap-2 flex-row items-center justify-between">
        <Button title={t('user-settings:edit_search')} onClick={onModalOpen} variant="ghost" />
        {frequencyEl}
      </div>
      {loading && (
        <div tw="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
          <Spinner color="primary" size="md" />
        </div>
      )}
    </div>
  );
};
export default SavedResearch;
